import { create } from 'zustand';
import type { VoicePreset } from '@/lib/voicePreset/types';
import { listVoicePresets } from '@/lib/voicePreset/api';

export type Mode = 'text' | 'vision' | 'ocr' | 'audio' | 'i2i' | 'novel';
export type OcrProcessingMode = 'ocr' | 'extract' | 'ai' | 'api';
export type OcrFormat = 'markdown' | 'text' | 'json';
export type ApiProvider = 'ollama' | 'gemini' | 'openai' | 'xai';
export type WhisperLanguage = 'ja' | 'en' | 'auto';
export type WhisperMode = 'fast' | 'balanced' | 'accurate';
export type I2iAspectRatio = 'none' | '1:1' | '16:9' | '9:16' | '4:3' | '3:4';
export type I2iModel = 'gemini-2.5-flash-image' | 'gemini-3-pro-image-preview';

export interface I2iConfig {
  aspectRatio: I2iAspectRatio;
  model: I2iModel;
}

// Animate page settings
export type AnimationType = 'mp4' | 'gif';
export type MotionType = 'sway' | 'circle' | 'zoom' | 'drift' | 'kenburns' | 'custom'
  | 'vertical' | 'horizontal' | 'perspective' | 'dolly'
  | 'zoom_left' | 'zoom_center' | 'zoom_right' | 'linear';
export type InterpolationMode = 'linear' | 'circular' | 'ease_in' | 'ease_out' | 'ease_in_out';


export interface AnimateConfig {
  animationType: AnimationType;
  motionType: MotionType;
  animationLength: number;
  outputFps: number;
  maxSize: number;
  gain: number;
  motionAmount: number;
  shiftScale: number;
  interpolation: InterpolationMode;
  reverse: boolean;
  loop: boolean;
}

interface PdfPage {
  pageNum: number;
  status: 'pending' | 'processing' | 'done' | 'error';
  originalText?: string;  // PDF embedded text (read-only)
  ocrResult?: string;     // YomiToku OCR result (read-only)
  aiResult?: string;      // AI processing result (editable)
  thumbnail?: string;
  finalText?: string;     // Final text (editable)
  // Processing status for each column (null = not processed, true = processed)
  extractProcessed?: boolean;  // Whether embed text extraction was attempted
  ocrProcessed?: boolean;      // Whether OCR was attempted
  aiProcessed?: boolean;       // Whether AI processing was attempted
}

interface AppState {
  // Mobile navigation
  isMobileNavOpen: boolean;
  setIsMobileNavOpen: (open: boolean) => void;

  // Page sidebar open callback (set by PageSidebar, used by Header)
  openPageSidebar: (() => void) | null;
  setOpenPageSidebar: (fn: (() => void) | null) => void;

  // Mode
  mode: Mode;
  setMode: (mode: Mode) => void;

  // Server status
  serverStatus: 'online' | 'offline' | 'checking';
  ocrServerStatus: 'online' | 'offline' | 'checking';
  ollamaServerStatus: 'online' | 'offline' | 'checking';
  depthServerStatus: 'online' | 'offline' | 'checking';
  rembgServerStatus: 'online' | 'offline' | 'checking';
  qwenTtsServerStatus: 'online' | 'offline' | 'checking';
  icrawlerServerStatus: 'online' | 'offline' | 'checking';
  videoDownloaderServerStatus: 'online' | 'offline' | 'checking';
  setServerStatus: (status: 'online' | 'offline' | 'checking') => void;
  setOcrServerStatus: (status: 'online' | 'offline' | 'checking') => void;
  setOllamaServerStatus: (status: 'online' | 'offline' | 'checking') => void;
  setDepthServerStatus: (status: 'online' | 'offline' | 'checking') => void;
  setRembgServerStatus: (status: 'online' | 'offline' | 'checking') => void;
  setQwenTtsServerStatus: (status: 'online' | 'offline' | 'checking') => void;
  setIcrawlerServerStatus: (status: 'online' | 'offline' | 'checking') => void;
  setVideoDownloaderServerStatus: (status: 'online' | 'offline' | 'checking') => void;
  aceStepServerStatus: 'online' | 'offline' | 'checking';
  setAceStepServerStatus: (status: 'online' | 'offline' | 'checking') => void;
  stableAudioServerStatus: 'online' | 'offline' | 'checking';
  setStableAudioServerStatus: (status: 'online' | 'offline' | 'checking') => void;
  reforgeServerStatus: 'online' | 'offline' | 'checking';
  setReforgeServerStatus: (status: 'online' | 'offline' | 'checking') => void;
  mossSoundEffectServerStatus: 'online' | 'offline' | 'checking';
  setMossSoundEffectServerStatus: (status: 'online' | 'offline' | 'checking') => void;
  bertVits2ServerStatus: 'online' | 'offline' | 'checking';
  setBertVits2ServerStatus: (status: 'online' | 'offline' | 'checking') => void;
  irodoriTtsServerStatus: 'online' | 'offline' | 'checking';
  setIrodoriTtsServerStatus: (status: 'online' | 'offline' | 'checking') => void;

  // Text mode
  model: string;
  setModel: (model: string) => void;
  systemPrompt: string;
  setSystemPrompt: (prompt: string) => void;
  userPrompt: string;
  setUserPrompt: (prompt: string) => void;
  textOutput: string;
  setTextOutput: (output: string) => void;
  isGenerating: boolean;
  setIsGenerating: (generating: boolean) => void;

  // Vision mode
  visionModel: string;
  setVisionModel: (model: string) => void;
  visionPrompt: string;
  setVisionPrompt: (prompt: string) => void;
  visionImage: File | null;
  setVisionImage: (file: File | null) => void;
  visionOutput: string;
  setVisionOutput: (output: string) => void;

  // OCR mode
  ocrProcessingMode: OcrProcessingMode;
  setOcrProcessingMode: (mode: OcrProcessingMode) => void;
  ocrFormat: OcrFormat;
  setOcrFormat: (format: OcrFormat) => void;
  ocrFile: File | null;
  setOcrFile: (file: File | null) => void;
  ocrOutput: string;
  setOcrOutput: (output: string) => void;
  isOcrProcessing: boolean;
  setIsOcrProcessing: (processing: boolean) => void;

  // PDF handling
  pdfPages: PdfPage[];
  setPdfPages: (pages: PdfPage[]) => void;
  updatePdfPage: (pageNum: number, updates: Partial<PdfPage>) => void;
  selectedPdfPage: number | null;
  setSelectedPdfPage: (page: number | null) => void;
  selectedPdfPages: number[];  // チェックボックスで選択されたページ番号のリスト
  setSelectedPdfPages: (pages: number[]) => void;

  // Page rotation (0, 90, 180, 270 degrees)
  pageRotations: Map<number, number>;
  setPageRotation: (pageNum: number, rotation: number) => void;
  clearPageRotations: () => void;
  hasRotations: () => boolean;

  // PDF loading progress
  pdfLoadingProgress: { current: number; total: number } | null;
  setPdfLoadingProgress: (progress: { current: number; total: number } | null) => void;
  thumbnailLoadingProgress: { current: number; total: number } | null;
  setThumbnailLoadingProgress: (progress: { current: number; total: number } | null) => void;

  // Settings
  ollamaUrl: string;
  setOllamaUrl: (url: string) => void;
  ocrServerUrl: string;
  setOcrServerUrl: (url: string) => void;

  // AI processing settings (Ollama local)
  aiModel: string;
  setAiModel: (model: string) => void;
  aiPromptTemplate: string;
  setAiPromptTemplate: (template: string) => void;
  aiSystemInstruction: string;
  setAiSystemInstruction: (instruction: string) => void;
  aiInputColumns: { originalText: boolean; ocrResult: boolean };
  setAiInputColumns: (columns: { originalText: boolean; ocrResult: boolean }) => void;

  // API processing settings (Ollama/Gemini/OpenAI)
  apiProvider: ApiProvider;
  setApiProvider: (provider: ApiProvider) => void;
  apiModel: string;
  setApiModel: (model: string) => void;
  apiPromptTemplate: string;
  setApiPromptTemplate: (template: string) => void;
  apiSystemInstruction: string;
  setApiSystemInstruction: (instruction: string) => void;
  apiInputColumns: { originalText: boolean; ocrResult: boolean };
  setApiInputColumns: (columns: { originalText: boolean; ocrResult: boolean }) => void;
  apiUseStreaming: boolean;
  setApiUseStreaming: (use: boolean) => void;
  apiUseGoogleSearch: boolean;
  setApiUseGoogleSearch: (use: boolean) => void;

  // Available models
  availableModels: string[];
  setAvailableModels: (models: string[]) => void;

  // Whisper transcription
  whisperServerUrl: string;
  setWhisperServerUrl: (url: string) => void;
  whisperServerStatus: 'online' | 'offline' | 'checking';
  setWhisperServerStatus: (status: 'online' | 'offline' | 'checking') => void;
  whisperLanguage: WhisperLanguage;
  setWhisperLanguage: (language: WhisperLanguage) => void;
  whisperMode: WhisperMode;
  setWhisperMode: (mode: WhisperMode) => void;
  audioFile: File | null;
  setAudioFile: (file: File | null) => void;
  transcriptionResult: string;
  setTranscriptionResult: (result: string) => void;
  isTranscribing: boolean;
  setIsTranscribing: (transcribing: boolean) => void;
  transcriptionProgress: number;
  setTranscriptionProgress: (progress: number) => void;

  // Voice Call
  voiceCallSelectedPersonaId: string | null;
  setVoiceCallSelectedPersonaId: (id: string | null) => void;
  voiceCallVadThreshold: number;
  setVoiceCallVadThreshold: (v: number) => void;
  voiceCallWarmupEnabled: boolean;
  setVoiceCallWarmupEnabled: (v: boolean) => void;
  voiceCallAvatarVisible: boolean;
  setVoiceCallAvatarVisible: (v: boolean) => void;

  // I2I mode settings
  i2iConfig: I2iConfig;
  setI2iConfig: (config: Partial<I2iConfig>) => void;
  i2iDefaultPrompt: string;
  setI2iDefaultPrompt: (prompt: string) => void;

  // ComfyUI selected workflow (for header display)
  comfyuiSelectedWorkflow: { name: string; category: string } | null;
  setComfyuiSelectedWorkflow: (workflow: { name: string; category: string } | null) => void;

  // Animate page settings
  animateConfig: AnimateConfig;
  setAnimateConfig: (config: Partial<AnimateConfig>) => void;

  // Eagle settings
  eagleApiUrl: string;
  setEagleApiUrl: (url: string) => void;
  eagleApiToken: string;
  setEagleApiToken: (token: string) => void;
  eagleServerStatus: 'online' | 'offline' | 'checking';
  setEagleServerStatus: (status: 'online' | 'offline' | 'checking') => void;

  // Eagle swipe-up action config
  eagleSwipeConfig: EagleSwipeConfig;
  setEagleSwipeConfig: (cfg: Partial<EagleSwipeConfig>) => void;

  // Voice preset cache
  voicePresets: VoicePreset[];
  voicePresetsLoaded: boolean;
  refreshVoicePresets: () => Promise<void>;
}

export type EagleSwipeUpAction = 'none' | 'toggleStar' | 'addToFolder';

export interface EagleSwipeConfig {
  upAction: EagleSwipeUpAction;
  targetFolderId: string | null;
  advanceAfterAction: boolean;
}

const DEFAULT_EAGLE_SWIPE_CONFIG: EagleSwipeConfig = {
  upAction: 'none',
  targetFolderId: null,
  advanceAfterAction: false,
};

function loadEagleSwipeConfig(): EagleSwipeConfig {
  if (typeof window === 'undefined') return DEFAULT_EAGLE_SWIPE_CONFIG;
  try {
    const raw = localStorage.getItem('eagle-swipe-config');
    if (!raw) return DEFAULT_EAGLE_SWIPE_CONFIG;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_EAGLE_SWIPE_CONFIG, ...parsed };
  } catch {
    return DEFAULT_EAGLE_SWIPE_CONFIG;
  }
}

export const useAppStore = create<AppState>((set) => ({
  // Mobile navigation
  isMobileNavOpen: false,
  setIsMobileNavOpen: (open) => set({ isMobileNavOpen: open }),

  // Page sidebar
  openPageSidebar: null,
  setOpenPageSidebar: (fn) => set({ openPageSidebar: fn }),

  // Mode
  mode: 'ocr',
  setMode: (mode) => set({ mode }),

  // Server status
  serverStatus: 'checking',
  ocrServerStatus: 'checking',
  ollamaServerStatus: 'checking',
  depthServerStatus: 'checking',
  rembgServerStatus: 'checking',
  qwenTtsServerStatus: 'checking',
  icrawlerServerStatus: 'checking',
  videoDownloaderServerStatus: 'checking',
  setServerStatus: (serverStatus) => set({ serverStatus }),
  setOcrServerStatus: (ocrServerStatus) => set({ ocrServerStatus }),
  setOllamaServerStatus: (ollamaServerStatus) => set({ ollamaServerStatus }),
  setDepthServerStatus: (depthServerStatus) => set({ depthServerStatus }),
  setRembgServerStatus: (rembgServerStatus) => set({ rembgServerStatus }),
  setQwenTtsServerStatus: (qwenTtsServerStatus) => set({ qwenTtsServerStatus }),
  setIcrawlerServerStatus: (icrawlerServerStatus) => set({ icrawlerServerStatus }),
  setVideoDownloaderServerStatus: (videoDownloaderServerStatus) => set({ videoDownloaderServerStatus }),
  aceStepServerStatus: 'checking',
  setAceStepServerStatus: (aceStepServerStatus) => set({ aceStepServerStatus }),
  stableAudioServerStatus: 'checking',
  setStableAudioServerStatus: (stableAudioServerStatus) => set({ stableAudioServerStatus }),
  reforgeServerStatus: 'checking',
  setReforgeServerStatus: (reforgeServerStatus) => set({ reforgeServerStatus }),
  mossSoundEffectServerStatus: 'checking',
  setMossSoundEffectServerStatus: (mossSoundEffectServerStatus) => set({ mossSoundEffectServerStatus }),
  bertVits2ServerStatus: 'checking',
  setBertVits2ServerStatus: (bertVits2ServerStatus) => set({ bertVits2ServerStatus }),
  irodoriTtsServerStatus: 'checking',
  setIrodoriTtsServerStatus: (irodoriTtsServerStatus) => set({ irodoriTtsServerStatus }),

  // Text mode
  model: 'gemma3:4b',
  setModel: (model) => set({ model }),
  systemPrompt: 'You are a helpful assistant.',
  setSystemPrompt: (systemPrompt) => set({ systemPrompt }),
  userPrompt: '',
  setUserPrompt: (userPrompt) => set({ userPrompt }),
  textOutput: '',
  setTextOutput: (textOutput) => set({ textOutput }),
  isGenerating: false,
  setIsGenerating: (isGenerating) => set({ isGenerating }),

  // Vision mode
  visionModel: 'gemma3:4b',
  setVisionModel: (visionModel) => set({ visionModel }),
  visionPrompt: 'What is in this image? Describe it in detail.',
  setVisionPrompt: (visionPrompt) => set({ visionPrompt }),
  visionImage: null,
  setVisionImage: (visionImage) => set({ visionImage }),
  visionOutput: '',
  setVisionOutput: (visionOutput) => set({ visionOutput }),

  // OCR mode
  ocrProcessingMode: 'extract',  // デフォルトを埋め込みテキストモードに変更
  setOcrProcessingMode: (ocrProcessingMode) => set({ ocrProcessingMode }),
  ocrFormat: 'markdown',
  setOcrFormat: (ocrFormat) => set({ ocrFormat }),
  ocrFile: null,
  setOcrFile: (ocrFile) => set({ ocrFile }),
  ocrOutput: '',
  setOcrOutput: (ocrOutput) => set({ ocrOutput }),
  isOcrProcessing: false,
  setIsOcrProcessing: (isOcrProcessing) => set({ isOcrProcessing }),

  // PDF handling
  pdfPages: [],
  setPdfPages: (pdfPages) => set({ pdfPages }),
  updatePdfPage: (pageNum, updates) => set((state) => ({
    pdfPages: state.pdfPages.map((page) =>
      page.pageNum === pageNum ? { ...page, ...updates } : page
    ),
  })),
  selectedPdfPage: null,
  setSelectedPdfPage: (selectedPdfPage) => set({ selectedPdfPage }),
  selectedPdfPages: [],
  setSelectedPdfPages: (selectedPdfPages) => set({ selectedPdfPages }),

  // Page rotation
  pageRotations: new Map(),
  setPageRotation: (pageNum, rotation) => set((state) => {
    const newRotations = new Map(state.pageRotations);
    if (rotation === 0) {
      newRotations.delete(pageNum);
    } else {
      newRotations.set(pageNum, rotation % 360);
    }
    return { pageRotations: newRotations };
  }),
  clearPageRotations: () => set({ pageRotations: new Map() }),
  hasRotations: () => false, // Will be computed in component

  // PDF loading progress
  pdfLoadingProgress: null,
  setPdfLoadingProgress: (pdfLoadingProgress) => set({ pdfLoadingProgress }),
  thumbnailLoadingProgress: null,
  setThumbnailLoadingProgress: (thumbnailLoadingProgress) => set({ thumbnailLoadingProgress }),

  // Settings
  ollamaUrl: 'http://192.168.11.50:11434',
  setOllamaUrl: (ollamaUrl) => set({ ollamaUrl }),
  ocrServerUrl: 'http://localhost:8765',
  setOcrServerUrl: (ocrServerUrl) => set({ ocrServerUrl }),

  // AI processing settings
  aiModel: typeof window !== 'undefined' ? localStorage.getItem('aiModel') || 'gemma3:4b' : 'gemma3:4b',
  setAiModel: (aiModel) => {
    set({ aiModel });
    if (typeof window !== 'undefined') localStorage.setItem('aiModel', aiModel);
  },
  aiPromptTemplate: typeof window !== 'undefined'
    ? localStorage.getItem('aiPromptTemplate') || '以下のテキストを200文字以内で要約してください:\n\n{text}'
    : '以下のテキストを200文字以内で要約してください:\n\n{text}',
  setAiPromptTemplate: (aiPromptTemplate) => {
    set({ aiPromptTemplate });
    if (typeof window !== 'undefined') localStorage.setItem('aiPromptTemplate', aiPromptTemplate);
  },
  aiSystemInstruction: typeof window !== 'undefined'
    ? localStorage.getItem('aiSystemInstruction') || 'あなたは優秀な文書要約アシスタントです。テキストの要点を簡潔にまとめることが得意です。'
    : 'あなたは優秀な文書要約アシスタントです。テキストの要点を簡潔にまとめることが得意です。',
  setAiSystemInstruction: (aiSystemInstruction) => {
    set({ aiSystemInstruction });
    if (typeof window !== 'undefined') localStorage.setItem('aiSystemInstruction', aiSystemInstruction);
  },
  aiInputColumns: { originalText: true, ocrResult: false },
  setAiInputColumns: (aiInputColumns) => set({ aiInputColumns }),

  // API processing settings (Ollama/Gemini/OpenAI)
  apiProvider: (typeof window !== 'undefined' ? localStorage.getItem('apiProvider') as ApiProvider : null) || 'gemini',
  setApiProvider: (apiProvider) => {
    set({ apiProvider });
    if (typeof window !== 'undefined') localStorage.setItem('apiProvider', apiProvider);
  },
  apiModel: typeof window !== 'undefined' ? localStorage.getItem('apiModel') || 'gemini-3.5-flash' : 'gemini-3.5-flash',
  setApiModel: (apiModel) => {
    set({ apiModel });
    if (typeof window !== 'undefined') localStorage.setItem('apiModel', apiModel);
  },
  apiPromptTemplate: typeof window !== 'undefined'
    ? localStorage.getItem('apiPromptTemplate') || '以下のテキストを200文字以内で要約してください:\n\n{text}'
    : '以下のテキストを200文字以内で要約してください:\n\n{text}',
  setApiPromptTemplate: (apiPromptTemplate) => {
    set({ apiPromptTemplate });
    if (typeof window !== 'undefined') localStorage.setItem('apiPromptTemplate', apiPromptTemplate);
  },
  apiSystemInstruction: typeof window !== 'undefined'
    ? localStorage.getItem('apiSystemInstruction') || 'あなたは優秀な文書要約アシスタントです。'
    : 'あなたは優秀な文書要約アシスタントです。',
  setApiSystemInstruction: (apiSystemInstruction) => {
    set({ apiSystemInstruction });
    if (typeof window !== 'undefined') localStorage.setItem('apiSystemInstruction', apiSystemInstruction);
  },
  apiInputColumns: { originalText: true, ocrResult: false },
  setApiInputColumns: (apiInputColumns) => set({ apiInputColumns }),
  apiUseStreaming: true,
  setApiUseStreaming: (apiUseStreaming) => set({ apiUseStreaming }),
  apiUseGoogleSearch: false,
  setApiUseGoogleSearch: (apiUseGoogleSearch) => set({ apiUseGoogleSearch }),

  // Available models
  availableModels: [],
  setAvailableModels: (availableModels) => set({ availableModels }),

  // Whisper transcription
  whisperServerUrl: typeof window !== 'undefined'
    ? localStorage.getItem('whisperServerUrl') || 'http://localhost:8766'
    : 'http://localhost:8766',
  setWhisperServerUrl: (whisperServerUrl) => {
    set({ whisperServerUrl });
    if (typeof window !== 'undefined') localStorage.setItem('whisperServerUrl', whisperServerUrl);
  },
  whisperServerStatus: 'checking',
  setWhisperServerStatus: (whisperServerStatus) => set({ whisperServerStatus }),
  whisperLanguage: (typeof window !== 'undefined' ? localStorage.getItem('whisperLanguage') as WhisperLanguage : null) || 'ja',
  setWhisperLanguage: (whisperLanguage) => {
    set({ whisperLanguage });
    if (typeof window !== 'undefined') localStorage.setItem('whisperLanguage', whisperLanguage);
  },
  whisperMode: (typeof window !== 'undefined' ? localStorage.getItem('whisperMode') as WhisperMode : null) || 'fast',
  setWhisperMode: (whisperMode) => {
    set({ whisperMode });
    if (typeof window !== 'undefined') localStorage.setItem('whisperMode', whisperMode);
  },
  voiceCallSelectedPersonaId: typeof window !== 'undefined'
    ? localStorage.getItem('voiceCallSelectedPersonaId') || null
    : null,
  setVoiceCallSelectedPersonaId: (voiceCallSelectedPersonaId) => {
    set({ voiceCallSelectedPersonaId });
    if (typeof window !== 'undefined') {
      if (voiceCallSelectedPersonaId) {
        localStorage.setItem('voiceCallSelectedPersonaId', voiceCallSelectedPersonaId);
      } else {
        localStorage.removeItem('voiceCallSelectedPersonaId');
      }
    }
  },
  voiceCallVadThreshold: typeof window !== 'undefined'
    ? Number(localStorage.getItem('voiceCallVadThreshold') || '0.5')
    : 0.5,
  setVoiceCallVadThreshold: (voiceCallVadThreshold) => {
    set({ voiceCallVadThreshold });
    if (typeof window !== 'undefined') {
      localStorage.setItem('voiceCallVadThreshold', String(voiceCallVadThreshold));
    }
  },
  voiceCallWarmupEnabled: typeof window !== 'undefined'
    ? localStorage.getItem('voiceCallWarmupEnabled') !== 'false'
    : true,
  setVoiceCallWarmupEnabled: (voiceCallWarmupEnabled) => {
    set({ voiceCallWarmupEnabled });
    if (typeof window !== 'undefined') {
      localStorage.setItem('voiceCallWarmupEnabled', String(voiceCallWarmupEnabled));
    }
  },
  voiceCallAvatarVisible: typeof window !== 'undefined'
    ? localStorage.getItem('voiceCallAvatarVisible') !== 'false'
    : true,
  setVoiceCallAvatarVisible: (voiceCallAvatarVisible) => {
    set({ voiceCallAvatarVisible });
    if (typeof window !== 'undefined') {
      localStorage.setItem('voiceCallAvatarVisible', String(voiceCallAvatarVisible));
    }
  },
  audioFile: null,
  setAudioFile: (audioFile) => set({ audioFile }),
  transcriptionResult: '',
  setTranscriptionResult: (transcriptionResult) => set({ transcriptionResult }),
  isTranscribing: false,
  setIsTranscribing: (isTranscribing) => set({ isTranscribing }),
  transcriptionProgress: 0,
  setTranscriptionProgress: (transcriptionProgress) => set({ transcriptionProgress }),

  // I2I mode settings
  i2iConfig: {
    aspectRatio: 'none',
    model: (typeof window !== 'undefined' ? localStorage.getItem('i2iModel') as any : null) || 'gemini-2.5-flash-image',
  },
  setI2iConfig: (config) => set((state) => {
    const newConfig = { ...state.i2iConfig, ...config };
    // Persist model selection to localStorage
    if (config.model && typeof window !== 'undefined') {
      localStorage.setItem('i2iModel', config.model);
    }
    return { i2iConfig: newConfig };
  }),
  i2iDefaultPrompt: typeof window !== 'undefined'
    ? localStorage.getItem('i2iDefaultPrompt') || ''
    : '',
  setI2iDefaultPrompt: (i2iDefaultPrompt) => {
    set({ i2iDefaultPrompt });
    if (typeof window !== 'undefined') localStorage.setItem('i2iDefaultPrompt', i2iDefaultPrompt);
  },

  // ComfyUI selected workflow
  comfyuiSelectedWorkflow: null,
  setComfyuiSelectedWorkflow: (comfyuiSelectedWorkflow) => set({ comfyuiSelectedWorkflow }),

  // Animate page settings
  animateConfig: typeof window !== 'undefined' && localStorage.getItem('animateConfig')
    ? JSON.parse(localStorage.getItem('animateConfig')!)
    : {
        animationType: 'gif',
        motionType: 'sway',
        animationLength: 2,
        outputFps: 15,
        maxSize: 512,
        gain: 1.5,
        motionAmount: 50,
        shiftScale: 1.0,
        interpolation: 'linear',
        reverse: false,
        loop: true,
      },
  setAnimateConfig: (config) => set((state) => {
    const newConfig = { ...state.animateConfig, ...config };
    if (typeof window !== 'undefined') {
      localStorage.setItem('animateConfig', JSON.stringify(newConfig));
    }
    return { animateConfig: newConfig };
  }),

  // Eagle settings
  eagleApiUrl: typeof window !== 'undefined'
    ? localStorage.getItem('eagleApiUrl') || 'http://localhost:41595'
    : 'http://localhost:41595',
  setEagleApiUrl: (eagleApiUrl) => {
    set({ eagleApiUrl });
    if (typeof window !== 'undefined') localStorage.setItem('eagleApiUrl', eagleApiUrl);
  },
  eagleApiToken: typeof window !== 'undefined'
    ? localStorage.getItem('eagleApiToken') || ''
    : '',
  setEagleApiToken: (eagleApiToken) => {
    set({ eagleApiToken });
    if (typeof window !== 'undefined') localStorage.setItem('eagleApiToken', eagleApiToken);
  },
  eagleServerStatus: 'checking',
  setEagleServerStatus: (eagleServerStatus) => set({ eagleServerStatus }),

  eagleSwipeConfig: loadEagleSwipeConfig(),
  setEagleSwipeConfig: (cfg) => set((state) => {
    const next = { ...state.eagleSwipeConfig, ...cfg };
    if (typeof window !== 'undefined') {
      localStorage.setItem('eagle-swipe-config', JSON.stringify(next));
    }
    return { eagleSwipeConfig: next };
  }),

  // Voice preset cache
  voicePresets: [],
  voicePresetsLoaded: false,
  refreshVoicePresets: async () => {
    const presets = await listVoicePresets();
    set({ voicePresets: presets, voicePresetsLoaded: true });
  },
}));
